/**
 * Trust Centre — the External access tab.
 *
 * Tenant-admin screen behind `trust.manage`. Two jobs:
 *   1. The external-user roster — invite, set role/expiry/named-grant, revoke.
 *   2. Self-service access requests — approve (which mints an external user) or
 *      deny.
 *
 * The one-time initial password returned when a user is created is surfaced in
 * a modal exactly once, with the portal link, for the admin to pass on
 * out-of-band. It is never stored in the client and never shown again.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { Card, CardHeader, Button, Modal, Field, Pill, Skeleton, ErrorNotice, type Tone } from '../../components/ui';
import { useTrustPortalT } from '../../lib/trustPortalStrings';
import {
  TRUST_EXTERNAL_ROLES,
  TRUST_ACCESS_DEFAULT_DAYS,
  type TrustExternalRole,
  type TrustExternalStatus,
} from '@opencomply/shared';

interface ExternalUser {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  role: TrustExternalRole;
  status: TrustExternalStatus;
  expiresAt: string | null;
  expired: boolean;
  ndaAcceptedAt: string | null;
  canAccessNamed: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface AccessRequest {
  id: string;
  email: string;
  company: string | null;
  reason: string | null;
  status: string;
  createdAt: string;
}

const STATUS_TONE: Record<TrustExternalStatus, Tone> = { INVITED: 'warning', ACTIVE: 'good', REVOKED: 'neutral' };

/** The one-time secret surfaced after creating a user. */
interface IssuedCredential {
  email: string;
  password: string;
}

export function TrustAccessTab({ slug }: { slug: string | null }) {
  const { tx, formatDate } = useTrustPortalT();
  const queryClient = useQueryClient();

  const users = useQuery({ queryKey: ['trust-external-users'], queryFn: () => api.get<ExternalUser[]>('/trust-centre/external-users') });
  const requests = useQuery({ queryKey: ['trust-access-requests'], queryFn: () => api.get<AccessRequest[]>('/trust-centre/access-requests?status=PENDING') });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [approveFor, setApproveFor] = useState<AccessRequest | null>(null);
  const [issued, setIssued] = useState<IssuedCredential | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Invite / approve form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState<TrustExternalRole>('CLIENT');
  const [days, setDays] = useState(TRUST_ACCESS_DEFAULT_DAYS);
  const [named, setNamed] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['trust-external-users'] });
    void queryClient.invalidateQueries({ queryKey: ['trust-access-requests'] });
  };

  const describe = (err: unknown): string => {
    if (err instanceof ApiError) {
      const key = `trust.access.error.${err.code}` as 'trust.access.error.UNKNOWN';
      const label = tx(key);
      return label.startsWith('trust.access.error.') ? tx('trust.access.error.UNKNOWN') : label;
    }
    return tx('trust.access.error.UNKNOWN');
  };

  const resetForm = () => { setEmail(''); setName(''); setCompany(''); setRole('CLIENT'); setDays(TRUST_ACCESS_DEFAULT_DAYS); setNamed(false); setError(null); };

  const invite = useMutation({
    mutationFn: () =>
      api.post<{ user: ExternalUser; initialPassword: string }>('/trust-centre/external-users', {
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        company: company.trim() || null,
        role,
        expiresInDays: days,
        canAccessNamed: named,
      }),
    onSuccess: (r) => {
      setInviteOpen(false);
      resetForm();
      setIssued({ email: r.user.email, password: r.initialPassword });
      invalidate();
    },
    onError: (e) => setError(describe(e)),
  });

  const approve = useMutation({
    mutationFn: (id: string) =>
      api.post<{ user: ExternalUser; initialPassword: string | null }>(`/trust-centre/access-requests/${id}/approve`, {
        role, expiresInDays: days, canAccessNamed: named,
      }),
    onSuccess: (r) => {
      setApproveFor(null);
      resetForm();
      if (r.initialPassword) setIssued({ email: r.user.email, password: r.initialPassword });
      invalidate();
    },
    onError: (e) => setError(describe(e)),
  });

  const deny = useMutation({
    mutationFn: (id: string) => api.post(`/trust-centre/access-requests/${id}/deny`, {}),
    onSuccess: invalidate,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/trust-centre/external-users/${id}/revoke`, {}),
    onSuccess: invalidate,
  });

  const roleLabel = (r: TrustExternalRole) => tx(`trust.access.role.${r}` as 'trust.access.role.CLIENT');
  const portalUrl = slug ? `${window.location.origin}/trust/${slug}/portal` : null;

  const openInvite = () => { resetForm(); setInviteOpen(true); };
  const openApprove = (req: AccessRequest) => { resetForm(); setEmail(req.email); setApproveFor(req); };

  const formFields = (showContact: boolean) => (
    <div className="space-y-1">
      {showContact && (
        <Field label={tx('trust.access.email')} htmlFor="eu-email">
          <input id="eu-email" type="email" className="oc-input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      )}
      {showContact && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={tx('trust.access.name')} htmlFor="eu-name">
            <input id="eu-name" className="oc-input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={tx('trust.access.company')} htmlFor="eu-company">
            <input id="eu-company" className="oc-input" value={company} onChange={(e) => setCompany(e.target.value)} />
          </Field>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={tx('trust.access.role')} htmlFor="eu-role">
          <select id="eu-role" className="oc-input" value={role} onChange={(e) => setRole(e.target.value as TrustExternalRole)}>
            {TRUST_EXTERNAL_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
          </select>
        </Field>
        <Field label={tx('trust.access.expiresInDays')} htmlFor="eu-days">
          <input id="eu-days" type="number" min={1} max={730} className="oc-input"
            value={days} onChange={(e) => setDays(Math.max(1, Math.min(730, Number(e.target.value) || 1)))} />
        </Field>
      </div>
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" className="mt-1 h-4 w-4 accent-[rgb(var(--brand))]" checked={named} onChange={(e) => setNamed(e.target.checked)} />
        <span className="min-w-0">
          <span className="block text-sm text-ink">{tx('trust.access.namedAccess')}</span>
          <span className="block text-xs text-ink-muted">{tx('trust.access.namedAccessHint')}</span>
        </span>
      </label>
      {error && <p className="text-sm text-critical" role="alert">{error}</p>}
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="oc-h2">{tx('trust.access.title')}</h2>
        <p className="oc-muted mt-1 max-w-2xl">{tx('trust.access.subtitle')}</p>
      </div>

      {/* --- Access requests ------------------------------------------------ */}
      <Card>
        <CardHeader title={tx('trust.access.requestsTitle')} />
        {requests.isLoading && <Skeleton className="h-16" />}
        {requests.data && requests.data.length === 0 && (
          <p className="text-sm text-ink-muted">{tx('trust.access.requestsEmpty')}</p>
        )}
        {requests.data && requests.data.length > 0 && (
          <ul className="space-y-2">
            {requests.data.map((r) => (
              <li key={r.id} className="rounded-xl border border-line p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-ink break-words">{r.email}</p>
                    {r.company && <p className="text-xs text-ink-muted">{r.company}</p>}
                    {r.reason && <p className="text-sm text-ink-secondary mt-1 break-words">{r.reason}</p>}
                    <p className="text-xs text-ink-muted mt-1">{tx('trust.access.requested', { date: formatDate(r.createdAt) })}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="primary" onClick={() => openApprove(r)}>{tx('trust.access.approve')}</Button>
                    <Button size="sm" variant="ghost" disabled={deny.isPending}
                      onClick={() => { if (window.confirm(tx('trust.access.denyConfirm'))) deny.mutate(r.id); }}>
                      {tx('trust.access.deny')}
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* --- External users ------------------------------------------------- */}
      <Card>
        <CardHeader title={tx('trust.access.usersTitle')} action={<Button size="sm" variant="primary" onClick={openInvite}>{tx('trust.access.invite')}</Button>} />
        {users.isLoading && <Skeleton className="h-24" />}
        {users.isError && <ErrorNotice message={tx('trust.access.usersLoadFailed')} onRetry={() => void users.refetch()} />}
        {users.data && users.data.length === 0 && <p className="text-sm text-ink-muted">{tx('trust.access.usersEmpty')}</p>}
        {users.data && users.data.length > 0 && (
          <ul className="space-y-2">
            {users.data.map((u) => (
              <li key={u.id} className="rounded-xl border border-line p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-ink break-words">{u.email}</p>
                    <p className="text-xs text-ink-muted">
                      {[u.name, u.company].filter(Boolean).join(' · ') || '—'}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <Pill tone="neutral">{roleLabel(u.role)}</Pill>
                      <Pill tone={u.expired ? 'neutral' : STATUS_TONE[u.status]}>
                        {u.expired ? tx('trust.access.statusExpired') : tx(`trust.access.status${u.status}` as 'trust.access.statusACTIVE')}
                      </Pill>
                      {u.canAccessNamed && <Pill tone="serious">{tx('trust.tier.NAMED')}</Pill>}
                    </div>
                    <p className="text-xs text-ink-muted mt-1.5">
                      {tx('trust.access.nda')}: {u.ndaAcceptedAt ? tx('trust.access.ndaAccepted', { date: formatDate(u.ndaAcceptedAt) }) : tx('trust.access.ndaPending')}
                      {' · '}
                      {tx('trust.access.expires')}: {u.expiresAt ? formatDate(u.expiresAt) : '—'}
                      {' · '}
                      {tx('trust.access.lastLogin')}: {u.lastLoginAt ? formatDate(u.lastLoginAt) : '—'}
                    </p>
                  </div>
                  {u.status !== 'REVOKED' && (
                    <Button size="sm" variant="ghost" disabled={revoke.isPending}
                      onClick={() => { if (window.confirm(tx('trust.access.revokeConfirm'))) revoke.mutate(u.id); }}>
                      {tx('trust.access.revoke')}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* --- Invite modal --------------------------------------------------- */}
      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={tx('trust.access.invite')}
        footer={
          <>
            <Button onClick={() => setInviteOpen(false)}>{tx('trust.access.cancel')}</Button>
            <Button variant="primary" disabled={invite.isPending || email.trim() === ''} onClick={() => invite.mutate()}>
              {tx('trust.access.create')}
            </Button>
          </>
        }
      >
        {formFields(true)}
      </Modal>

      {/* --- Approve modal -------------------------------------------------- */}
      <Modal
        open={approveFor !== null}
        onClose={() => setApproveFor(null)}
        title={tx('trust.access.approveTitle')}
        footer={
          <>
            <Button onClick={() => setApproveFor(null)}>{tx('trust.access.cancel')}</Button>
            <Button variant="primary" disabled={approve.isPending} onClick={() => approveFor && approve.mutate(approveFor.id)}>
              {tx('trust.access.approve')}
            </Button>
          </>
        }
      >
        {approveFor && <p className="mb-3 text-ink-secondary break-words">{approveFor.email}</p>}
        {formFields(false)}
      </Modal>

      {/* --- One-time credential modal -------------------------------------- */}
      <Modal
        open={issued !== null}
        onClose={() => setIssued(null)}
        title={tx('trust.access.passwordTitle')}
        footer={<Button variant="primary" onClick={() => setIssued(null)}>{tx('trust.access.passwordDone')}</Button>}
      >
        {issued && (
          <div className="space-y-3">
            <p className="text-ink-secondary">{tx('trust.access.passwordBody', { email: issued.email })}</p>
            <input readOnly value={issued.password} onFocus={(e) => e.currentTarget.select()}
              className="oc-input font-mono text-sm" aria-label={tx('trust.access.passwordTitle')} />
            {portalUrl && (
              <div>
                <p className="oc-label">{tx('trust.access.portalLink')}</p>
                <input readOnly value={portalUrl} onFocus={(e) => e.currentTarget.select()}
                  className="oc-input font-mono text-xs" aria-label={tx('trust.access.portalLink')} />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
